import { useId, useState, type ReactNode } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type AutocompleteInputProps<T> = {
  value: string;
  onValueChange: (value: string) => void;
  suggestions: T[];
  getSuggestionKey: (suggestion: T) => string;
  renderSuggestion: (suggestion: T) => ReactNode;
  onSelect: (suggestion: T) => void;
  placeholder?: string;
  isLoading?: boolean;
  // Shown under the input when the query is long enough but nothing matched.
  emptyMessage?: string;
  showEmptyMessage?: boolean;
  invalid?: boolean;
  ariaLabel: string;
  onBlur?: () => void;
};

/**
 * Text input with a suggestion list (WAI-ARIA combobox): arrow keys move the
 * highlight, Enter selects, Escape closes.
 */
export const AutocompleteInput = <T,>({
  value,
  onValueChange,
  suggestions,
  getSuggestionKey,
  renderSuggestion,
  onSelect,
  placeholder,
  isLoading,
  emptyMessage,
  showEmptyMessage,
  invalid,
  ariaLabel,
  onBlur,
}: AutocompleteInputProps<T>) => {
  const listId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const hasSuggestions = suggestions.length > 0;
  const showList = isOpen && (hasSuggestions || !!showEmptyMessage);

  const select = (suggestion: T) => {
    onSelect(suggestion);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!hasSuggestions) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(
        (index) => (index - 1 + suggestions.length) % suggestions.length,
      );
    } else if (event.key === 'Enter' && isOpen && highlightedIndex >= 0) {
      event.preventDefault();
      select(suggestions[highlightedIndex]);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative w-full">
      <Input
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          showList && highlightedIndex >= 0
            ? `${listId}-${highlightedIndex}`
            : undefined
        }
        aria-invalid={invalid || undefined}
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onValueChange(event.target.value);
          setIsOpen(true);
          setHighlightedIndex(-1);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          setIsOpen(false);
          onBlur?.();
        }}
        onKeyDown={handleKeyDown}
      />
      {isLoading && (
        <span
          className="absolute right-4 top-1/2 size-4 -translate-y-1/2 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-900"
          aria-hidden
        />
      )}
      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 shadow-md"
        >
          {hasSuggestions ? (
            suggestions.map((suggestion, index) => (
              <li
                key={getSuggestionKey(suggestion)}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === highlightedIndex}
                className={cn(
                  'cursor-pointer rounded-md px-3 py-2 text-sm text-indigo-900',
                  index === highlightedIndex
                    ? 'bg-blue-50'
                    : 'hover:bg-gray-100',
                )}
                // mousedown fires before the input blur that closes the list.
                onMouseDown={(event) => {
                  event.preventDefault();
                  select(suggestion);
                }}
              >
                {renderSuggestion(suggestion)}
              </li>
            ))
          ) : (
            <li className="px-3 py-2 text-sm text-gray-500">{emptyMessage}</li>
          )}
        </ul>
      )}
    </div>
  );
};
